/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Model Lab AI Manager (per model asset)
 *
 * Split-screen layout:
 *   - Left:  Embedded AgentChat with the Model Lab Builder agent
 *   - Right: Asset-scoped context panel (versions, jobs, deployments for THIS asset)
 *
 * Route: /ai/ml/models/:assetId/manager
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import InsightsIcon from '@mui/icons-material/Insights';
import MemoryIcon from '@mui/icons-material/Memory';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScienceIcon from '@mui/icons-material/Science';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { AgentChat } from '../agent/AgentChat';
import NeuralNetwork3D, { type Layer3DSpec } from './NeuralNetwork3D';
import {
  agentApi,
  modelLabAssetApi,
  modelLabDeploymentApi,
  modelLabTrainingJobApi,
  modelLabVersionApi,
} from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import { useAuth } from '../../context/AuthContext';
import { ErrorAlert, LoadingSpinner } from '../common';
import { modelStageColor } from './utils';
import PageBackButton from '../../../components/PageBackButton';

const SIDE_PANEL_TABS = [
  { value: 'overview', label: 'Overview', icon: <MemoryIcon fontSize="small" /> },
  { value: 'architecture', label: 'Architecture', icon: <AccountTreeIcon fontSize="small" /> },
  { value: 'internals', label: 'Internals', icon: <InsightsIcon fontSize="small" /> },
  { value: 'versions', label: 'Versions', icon: <ViewListIcon fontSize="small" /> },
  { value: 'jobs', label: 'Training Jobs', icon: <ScienceIcon fontSize="small" /> },
  { value: 'deployments', label: 'Deployments', icon: <RocketLaunchIcon fontSize="small" /> },
] as const;

type PanelTab = (typeof SIDE_PANEL_TABS)[number]['value'];

const ModelLabAssetManagerPage: React.FC = () => {
  const { assetId: assetIdParam } = useParams<{ assetId: string }>();
  const assetId = Number(assetIdParam);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { user } = useAuth();
  const initialTab = (searchParams.get('tab') as PanelTab) || 'overview';
  const [tab, setTabState] = useState<PanelTab>(
    SIDE_PANEL_TABS.some((t) => t.value === initialTab) ? initialTab : 'overview',
  );
  const setTab = (next: PanelTab) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const assetQuery = useQuery({
    queryKey: ['model-lab-asset', assetId],
    queryFn: () => modelLabAssetApi.get(assetId),
    enabled: !!assetId,
  });

  const versionsQuery = useQuery({
    queryKey: ['model-lab-asset-versions', assetId],
    queryFn: () => modelLabVersionApi.list({ model_asset: assetId, page_size: 50 }),
    enabled: !!assetId,
  });

  const jobsQuery = useQuery({
    queryKey: ['model-lab-asset-jobs', assetId],
    queryFn: () => modelLabTrainingJobApi.list({ model_asset: assetId, page_size: 20 }),
    enabled: !!assetId,
  });

  const deploymentsQuery = useQuery({
    queryKey: ['model-lab-asset-deployments', assetId],
    queryFn: () =>
      modelLabDeploymentApi.list({ model_asset: assetId, page_size: 20 }),
    enabled: !!assetId,
  });

  const managerAgentQuery = useQuery({
    queryKey: ['model-lab-builder-agent'],
    queryFn: () => agentApi.getModelLabBuilder(),
    enabled: !!user?.is_staff,
    retry: false,
  });

  const latestVersion = useMemo(() => {
    const results = versionsQuery.data?.results || [];
    return [...results].sort((a, b) => b.version_number - a.version_number)[0];
  }, [versionsQuery.data]);

  const productionVersion = useMemo(
    () => (versionsQuery.data?.results || []).find((v) => v.stage === 'PRODUCTION'),
    [versionsQuery.data],
  );

  if (!user?.is_staff) {
    return (
      <Box p={3}>
        <Alert severity="error">
          The AI Manager is restricted to admin users.
        </Alert>
      </Box>
    );
  }

  if (assetQuery.isLoading) {
    return <LoadingSpinner message="Loading model asset..." />;
  }
  if (assetQuery.error) {
    return <ErrorAlert error={assetQuery.error as Error} />;
  }
  const asset = assetQuery.data;
  if (!asset) return null;

  const managerAgent = managerAgentQuery.data;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        minHeight: 0,
      }}
    >
      {/* Header */}
      <Paper
        square
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, md: 3 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        <PageBackButton label="Back to Registry" to="/ai/ml/models" preferHistory={false} hideLabelOnMobile />
        <AutoAwesomeIcon color="secondary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="h6" sx={{ lineHeight: 1.2, mr: 0.5 }} noWrap>
              AI Manager
            </Typography>
            <Typography variant="body1" color="text.secondary" noWrap>
              · {asset.name}
            </Typography>
            <Chip size="small" label={asset.family} />
            <Chip size="small" label={asset.framework} />
            <Chip size="small" label={asset.problem_type} variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {asset.description || 'No description'}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate('/ai/ml/models')}
        >
          Edit
        </Button>
        {managerAgent && (
          <Tooltip title="Open agent full-page">
            <IconButton
              size="small"
              onClick={() => navigate(`/ai/agents/${managerAgent.id}/chat`)}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      {/* Body: split pane */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: isDesktop ? 'row' : 'column',
          overflow: 'hidden',
        }}
      >
        {/* Left: chat */}
        <Box
          sx={{
            flex: isDesktop ? '1 1 60%' : '1 1 auto',
            minWidth: 0,
            minHeight: isDesktop ? 0 : '60vh',
            borderRight: isDesktop ? 1 : 0,
            borderBottom: isDesktop ? 0 : 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {managerAgentQuery.isLoading && <LinearProgress />}
          {managerAgentQuery.error && (
            <Box p={3}>
              <Alert severity="error">
                Failed to load the Model Lab Builder agent. Ensure the template has been
                provisioned for your user.
              </Alert>
            </Box>
          )}
          {managerAgent && (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <AgentChat agentIdProp={managerAgent.id} hideHeader />
            </Box>
          )}
        </Box>

        {/* Right: asset context */}
        <Box
          sx={{
            flex: isDesktop ? '1 1 40%' : '1 1 auto',
            minWidth: 0,
            maxWidth: isDesktop ? 520 : 'none',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          >
            {SIDE_PANEL_TABS.map((t) => (
              <Tab
                key={t.value}
                value={t.value}
                label={t.label}
                icon={t.icon}
                iconPosition="start"
                sx={{ minHeight: 40 }}
              />
            ))}
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
            {tab === 'overview' && (
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Asset
                  </Typography>
                  <Typography variant="h6">{asset.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {asset.description || 'No description'}
                  </Typography>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.5}>
                    <Row k="Family" v={asset.family} />
                    <Row k="Framework" v={asset.framework} />
                    <Row k="Problem type" v={asset.problem_type} />
                    <Row
                      k="Authoring"
                      v={asset.runtime_spec?.authoring_mode || 'no_code'}
                    />
                    <Row k="Executor" v={asset.runtime_spec?.executor || 'cpu'} />
                    <Row
                      k="Tags"
                      v={(asset.tags || []).join(', ') || '—'}
                    />
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Version status
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    <Row
                      k="Latest"
                      v={
                        latestVersion
                          ? `v${latestVersion.version_number} · ${latestVersion.stage}`
                          : 'None yet'
                      }
                    />
                    <Row
                      k="Production"
                      v={
                        productionVersion
                          ? `v${productionVersion.version_number}`
                          : 'Not promoted'
                      }
                    />
                    <Row
                      k="Training jobs"
                      v={String(jobsQuery.data?.count ?? 0)}
                    />
                    <Row
                      k="Deployments"
                      v={String(deploymentsQuery.data?.count ?? 0)}
                    />
                  </Stack>
                </Paper>
                <Alert severity="info" variant="outlined">
                  Ask the AI Manager to inspect, train, promote, or deploy this model.
                  Example prompts:
                  <Box component="ul" sx={{ m: 0, pl: 3, mt: 0.5 }}>
                    <li>"Summarize the status of {asset.name}"</li>
                    <li>"Run a new training job on the latest dataset version"</li>
                    <li>"Promote the best version to production"</li>
                  </Box>
                </Alert>
              </Stack>
            )}

            {tab === 'architecture' && (
              <ArchitectureView
                asset={asset}
                jobs={jobsQuery.data?.results || []}
                versions={versionsQuery.data?.results || []}
              />
            )}

            {tab === 'internals' && (
              <InternalsView
                asset={asset}
                jobs={jobsQuery.data?.results || []}
                versions={versionsQuery.data?.results || []}
              />
            )}

            {tab === 'versions' && (
              <VersionsList
                loading={versionsQuery.isLoading}
                versions={versionsQuery.data?.results || []}
              />
            )}

            {tab === 'jobs' && (
              <JobsList
                loading={jobsQuery.isLoading}
                jobs={jobsQuery.data?.results || []}
              />
            )}

            {tab === 'deployments' && (
              <DeploymentsList
                loading={deploymentsQuery.isLoading}
                deployments={deploymentsQuery.data?.results || []}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {k}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
      {v}
    </Typography>
  </Stack>
);

const VersionsList: React.FC<{ loading: boolean; versions: any[] }> = ({
  loading,
  versions,
}) => {
  if (loading) return <LinearProgress />;
  if (versions.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No versions yet. Train a model to create the first version.
      </Alert>
    );
  }
  return (
    <Stack spacing={1.5}>
      {versions.map((v) => (
        <Paper key={v.id} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2">v{v.version_number}</Typography>
            <Chip size="small" label={v.stage} color={modelStageColor(v.stage)} />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {formatStoredDateTime(v.created_at, undefined, '-')}
            </Typography>
          </Stack>
          {v.metrics && Object.keys(v.metrics).length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {Object.entries(v.metrics).slice(0, 6).map(([k, val]) => (
                <Chip
                  key={k}
                  size="small"
                  variant="outlined"
                  label={`${k}: ${typeof val === 'number' ? val.toFixed(4) : String(val)}`}
                />
              ))}
            </Stack>
          )}
        </Paper>
      ))}
    </Stack>
  );
};

const JOB_STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  DRAFT: 'default',
  QUEUED: 'warning',
  RUNNING: 'primary',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

const JobsList: React.FC<{ loading: boolean; jobs: any[] }> = ({ loading, jobs }) => {
  if (loading) return <LinearProgress />;
  if (jobs.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No training jobs yet for this asset.
      </Alert>
    );
  }
  return (
    <Stack spacing={1.5}>
      {jobs.map((j) => (
        <Paper key={j.id} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2">#{j.id}</Typography>
            <Chip
              size="small"
              label={j.status}
              color={JOB_STATUS_COLOR[j.status] || 'default'}
            />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {j.started_at
                ? formatStoredDateTime(j.started_at, undefined, '-')
                : formatStoredDateTime(j.created_at, undefined, '-')}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Dataset: {j.dataset_version_name || j.dataset_version}
            {j.feature_set_name ? ` · Features: ${j.feature_set_name}` : ''}
          </Typography>
          {j.error_message && (
            <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
              {j.error_message.slice(0, 160)}
            </Typography>
          )}
        </Paper>
      ))}
    </Stack>
  );
};

const DEPLOY_STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  PAUSED: 'warning',
  FAILED: 'error',
};

const DeploymentsList: React.FC<{ loading: boolean; deployments: any[] }> = ({
  loading,
  deployments,
}) => {
  if (loading) return <LinearProgress />;
  if (deployments.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No deployments yet. Promote a version and deploy it to serve predictions.
      </Alert>
    );
  }
  return (
    <Stack spacing={1.5}>
      {deployments.map((d) => (
        <Paper key={d.id} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
              {d.name}
            </Typography>
            <Chip
              size="small"
              label={d.status}
              color={DEPLOY_STATUS_COLOR[d.status] || 'default'}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Version: {d.model_version_name} · Endpoint: /{d.endpoint_slug}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
};

/* ─────────────── Architecture visualization ─────────────── */

const BLOCK_COLOR: Record<string, string> = {
  input: '#1976d2',
  preprocessing: '#ed6c02',
  estimator: '#2e7d32',
  ensemble: '#7b1fa2',
  meta_learner: '#c2185b',
  output: '#0288d1',
};

const humanize = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Treat a canvas architecture object as "empty" if it carries no estimator info. */
const isEmptyArch = (arch: any): boolean => {
  if (!arch) return true;
  const hasSingle = !!arch.single_estimator?.algorithm;
  const hasBases = Array.isArray(arch.base_estimators) && arch.base_estimators.length > 0;
  const hasPre = Array.isArray(arch.preprocessing) && arch.preprocessing.length > 0;
  return !hasSingle && !hasBases && !hasPre;
};

const FlowArrow: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
    <Box
      sx={{
        width: 2,
        height: 22,
        bgcolor: 'divider',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -4,
          left: -3,
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: '6px solid',
          borderTopColor: 'divider',
        },
      }}
    />
  </Box>
);

const ArchBlock: React.FC<{
  color: string;
  title: string;
  subtitle?: string;
  params?: Record<string, any>;
}> = ({ color, title, subtitle, params }) => (
  <Paper
    variant="outlined"
    sx={{
      borderLeft: `4px solid ${color}`,
      p: 1.25,
    }}
  >
    <Typography variant="subtitle2" sx={{ color, fontWeight: 700, fontSize: 13 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" display="block">
        {subtitle}
      </Typography>
    )}
    {params && Object.keys(params).length > 0 && (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
        {Object.entries(params).map(([k, v]) => (
          <Chip
            key={k}
            size="small"
            variant="outlined"
            label={`${k}: ${String(v)}`}
            sx={{ height: 20, fontSize: 11 }}
          />
        ))}
      </Stack>
    )}
  </Paper>
);

const ArchitectureView: React.FC<{ asset: any; jobs: any[]; versions: any[] }> = ({
  asset,
  jobs,
  versions,
}) => {
  const rs = asset.runtime_spec || {};
  const authoring = rs.authoring || {};
  const mode = rs.authoring_mode || authoring.mode || 'no_code';
  const noCode = authoring.no_code || {};
  const python = authoring.python || {};
  let arch = noCode.architecture || null;
  let features: string[] = noCode.feature_columns || [];
  let target: string = noCode.target_name || '';
  let preprocessingNames: string[] = noCode.preprocessing_steps || [];
  let derivedFromJob = false;

  // Fallback: if the asset has no canvas architecture, derive it from the most
  // recent COMPLETED (or any) training job config — this covers assets created
  // programmatically via scripts / direct API calls where the pipeline info
  // lives on the job config instead of the asset blueprint.
  const latestJob = useMemo(() => {
    const completed = jobs.find((j) => j.status === 'COMPLETED');
    return completed || jobs[0];
  }, [jobs]);

  if ((!arch || isEmptyArch(arch)) && latestJob?.config) {
    const cfg = latestJob.config;
    const hp = cfg.hyperparameters || {};
    const pre = cfg.preprocessing || {};
    const preSteps: any[] = [];
    if (pre.imputer && pre.imputer !== 'none') {
      preSteps.push({ type: `imputer_${pre.imputer}`, label: `Imputer (${pre.imputer})`, params: {} });
    }
    if (pre.scaler && pre.scaler !== 'none') {
      preSteps.push({ type: `${pre.scaler}_scaler`, label: `${humanize(pre.scaler)} Scaler`, params: {} });
    }
    if (pre.feature_selection && pre.feature_selection !== 'none') {
      preSteps.push({
        type: pre.feature_selection,
        label: humanize(pre.feature_selection),
        params: pre.feature_selection_params || {},
      });
    }
    arch = {
      preprocessing: preSteps,
      model_type: cfg.model_type || 'single',
      single_estimator: cfg.algorithm
        ? { algorithm: cfg.algorithm, params: hp }
        : null,
      voting_strategy: cfg.voting_strategy || 'hard',
      base_estimators: cfg.base_estimators || [],
      meta_learner: cfg.meta_learner || null,
    };
    if (!features.length && Array.isArray(cfg.feature_columns)) {
      features = cfg.feature_columns;
    }
    if (!target && cfg.target_column) {
      target = cfg.target_column;
    }
    derivedFromJob = true;
  }

  if (!target) target = '\u2014';

  if (mode === 'python' || !arch) {
    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Authoring mode
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {mode === 'python'
              ? 'Restricted Python recipe'
              : 'No architecture defined yet'}
          </Typography>
          {python.entrypoint && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Entrypoint: <code>{python.entrypoint}</code>
            </Typography>
          )}
          {python.allowed_libraries?.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {python.allowed_libraries.map((lib: string) => (
                <Chip key={lib} size="small" label={lib} />
              ))}
            </Stack>
          )}
        </Paper>
        {features.length > 0 && <FeaturesCard features={features} target={target} />}
        {!arch && mode !== 'python' && (
          <Alert severity="info" variant="outlined">
            This asset has no visual pipeline yet. Edit it to configure layers and estimators.
          </Alert>
        )}
      </Stack>
    );
  }

  const modelType: 'single' | 'voting' | 'stacking' = arch.model_type || 'single';
  const preprocessing = arch.preprocessing || [];
  const single = arch.single_estimator;
  const baseEstimators = arch.base_estimators || [];
  const meta = arch.meta_learner;
  const votingStrategy = arch.voting_strategy || 'hard';

  const inputSubtitle = features.length
    ? `${features.length} feature${features.length === 1 ? '' : 's'} · target: ${target}`
    : `target: ${target}`;

  return (
    <Stack spacing={2}>
      {derivedFromJob && (
        <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
          Visualizing from the latest training job config — this asset has no visual
          blueprint stored on the registry yet.
        </Alert>
      )}
      <FeaturesCard features={features} target={target} />

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <AccountTreeIcon fontSize="small" color="secondary" />
          <Typography variant="subtitle2">Pipeline</Typography>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label={modelType === 'single' ? 'Single' : modelType === 'voting' ? `Voting (${votingStrategy})` : 'Stacking'}
            color={modelType === 'single' ? 'default' : 'secondary'}
            variant="outlined"
          />
        </Stack>

        {/* Input */}
        <ArchBlock
          color={BLOCK_COLOR.input}
          title="Input"
          subtitle={inputSubtitle}
        />

        {/* Preprocessing */}
        {preprocessing.length > 0 && (
          <>
            {preprocessing.map((step: any, idx: number) => (
              <React.Fragment key={idx}>
                <FlowArrow />
                <ArchBlock
                  color={BLOCK_COLOR.preprocessing}
                  title={step.label || humanize(step.type || 'Step')}
                  subtitle={step.type}
                  params={step.params}
                />
              </React.Fragment>
            ))}
          </>
        )}
        {preprocessing.length === 0 && preprocessingNames.length > 0 && (
          <>
            <FlowArrow />
            <ArchBlock
              color={BLOCK_COLOR.preprocessing}
              title="Preprocessing"
              subtitle={preprocessingNames.join(' → ')}
            />
          </>
        )}

        {/* Estimator(s) */}
        <FlowArrow />
        {modelType === 'single' && single && (
          <ArchBlock
            color={BLOCK_COLOR.estimator}
            title={humanize(single.algorithm || 'Estimator')}
            subtitle="Single estimator"
            params={single.params}
          />
        )}
        {modelType === 'voting' && (
          <>
            <ArchBlock
              color={BLOCK_COLOR.ensemble}
              title={`Voting Ensemble (${votingStrategy})`}
              subtitle={`${baseEstimators.length} estimator${baseEstimators.length === 1 ? '' : 's'}`}
            />
            <Box sx={{ mt: 1, pl: 2, borderLeft: `2px dashed`, borderColor: 'divider' }}>
              <Stack spacing={1}>
                {baseEstimators.map((e: any, i: number) => (
                  <ArchBlock
                    key={i}
                    color={BLOCK_COLOR.estimator}
                    title={humanize(e.algorithm)}
                    subtitle={`Base estimator #${i + 1}`}
                    params={e.params}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
        {modelType === 'stacking' && (
          <>
            <ArchBlock
              color={BLOCK_COLOR.ensemble}
              title="Stacking Ensemble"
              subtitle={`${baseEstimators.length} base + 1 meta`}
            />
            <Box sx={{ mt: 1, pl: 2, borderLeft: `2px dashed`, borderColor: 'divider' }}>
              <Stack spacing={1}>
                {baseEstimators.map((e: any, i: number) => (
                  <ArchBlock
                    key={i}
                    color={BLOCK_COLOR.estimator}
                    title={humanize(e.algorithm)}
                    subtitle={`Base estimator #${i + 1}`}
                    params={e.params}
                  />
                ))}
              </Stack>
            </Box>
            {meta && (
              <>
                <FlowArrow />
                <ArchBlock
                  color={BLOCK_COLOR.meta_learner}
                  title={humanize(meta.algorithm || 'Meta Learner')}
                  subtitle="Meta learner"
                  params={meta.params}
                />
              </>
            )}
          </>
        )}

        {/* Output */}
        <FlowArrow />
        <ArchBlock
          color={BLOCK_COLOR.output}
          title="Output"
          subtitle={humanize(asset.problem_type || 'prediction')}
        />
      </Paper>
    </Stack>
  );
};

const FeaturesCard: React.FC<{ features: string[]; target: string }> = ({
  features,
  target,
}) => (
  <Paper variant="outlined" sx={{ p: 1.5 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2">Features</Typography>
      <Chip size="small" label={`${features.length} input${features.length === 1 ? '' : 's'}`} />
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption" color="text.secondary">
        target: <b>{target}</b>
      </Typography>
    </Stack>
    {features.length === 0 ? (
      <Typography variant="caption" color="text.secondary">
        No explicit feature list — features will be inferred from the dataset at train time.
      </Typography>
    ) : (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {features.map((f) => (
          <Chip
            key={f}
            size="small"
            label={f}
            variant="outlined"
            sx={{ height: 22, fontSize: 11 }}
          />
        ))}
      </Stack>
    )}
  </Paper>
);

/* ─────────────── Model Internals visualization ─────────────── */

const METRIC_COLOR = '#ffb020';
const IMPORTANCE_COLOR = '#7b1fa2';

const DL_ALGOS = new Set([
  'lstm', 'bilstm', 'gru', 'xlstm', 'transformer', 'temporal_fusion_transformer',
  'informer', 'patch_tst', 'cnn_1d', 'tcn', 'conv_lstm', 'wavenet', 'nbeats',
  'autoencoder', 'vae', 'attention_network', 'mlp',
]);

const TREE_ALGOS = new Set([
  'random_forest', 'gradient_boosting', 'extra_trees', 'adaboost',
  'decision_tree', 'xgboost', 'lightgbm',
]);

/**
 * Normalize algorithm names into one of the known DL/tree keys.
 * Handles sklearn class names (RandomForestClassifier), snake/camel/Pascal
 * casing, and common aliases (e.g. "rf", "xgb", "lgbm").
 */
const normalizeAlgorithm = (raw: string): string => {
  const s = (raw || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!s) return '';
  // DL mappings
  if (s.includes('bilstm')) return 'bilstm';
  if (s.includes('lstm')) return s.includes('conv') ? 'conv_lstm' : 'lstm';
  if (s.includes('gru')) return 'gru';
  if (s.includes('xlstm')) return 'xlstm';
  if (s.includes('transformer')) return 'transformer';
  if (s.includes('tft') || s.includes('temporalfusion')) return 'temporal_fusion_transformer';
  if (s.includes('informer')) return 'informer';
  if (s.includes('patchtst')) return 'patch_tst';
  if (s.includes('tcn')) return 'tcn';
  if (s.includes('wavenet')) return 'wavenet';
  if (s.includes('nbeats')) return 'nbeats';
  if (s.includes('autoencoder') || s === 'ae') return 'autoencoder';
  if (s === 'vae' || s.includes('variationalautoencoder')) return 'vae';
  if (s.includes('attention')) return 'attention_network';
  if (s.includes('cnn') || s.includes('conv1d') || s.includes('conv1') || s.includes('convnet')) return 'cnn_1d';
  if (s === 'mlp' || s.includes('multilayerperceptron') || s.includes('feedforward') || s.includes('dense')) return 'mlp';
  // Tree mappings
  if (s.includes('randomforest') || s === 'rf' || s === 'rfclassifier' || s === 'rfregressor') return 'random_forest';
  if (s.includes('extratrees') || s === 'et') return 'extra_trees';
  if (s.includes('gradientboost') || s === 'gbm' || s === 'gbr' || s === 'gbc') return 'gradient_boosting';
  if (s.includes('adaboost')) return 'adaboost';
  if (s.includes('xgb') || s.includes('xgboost')) return 'xgboost';
  if (s.includes('lightgbm') || s === 'lgbm' || s === 'lgb') return 'lightgbm';
  if (s.includes('decisiontree') || s === 'dt') return 'decision_tree';
  return s;
};

const fmtMetric = (v: any) =>
  typeof v === 'number' ? (Math.abs(v) < 1 ? v.toFixed(4) : v.toFixed(2)) : String(v);

const InternalsView: React.FC<{ asset: any; jobs: any[]; versions: any[] }> = ({
  asset,
  jobs,
  versions,
}) => {
  // Pick the newest version that has metrics; fall back to latest overall.
  const version = useMemo(() => {
    const sorted = [...versions].sort((a, b) => b.version_number - a.version_number);
    return sorted.find((v) => v.metrics && Object.keys(v.metrics).length > 0) || sorted[0];
  }, [versions]);

  const sourceJob = useMemo(() => {
    if (!version) return null;
    return jobs.find((j) => j.generated_model_version_id === version.id) || null;
  }, [jobs, version]);

  if (!version) {
    return (
      <Alert severity="info" variant="outlined">
        No trained versions yet. Launch a training job from the AI Manager chat to see
        model internals, feature importances, and performance charts.
      </Alert>
    );
  }

  const metrics = version.metrics || {};
  const summary = version.training_summary || {};
  const rawAlgorithm: string = (summary.algorithm || sourceJob?.config?.algorithm || asset?.runtime_spec?.algorithm || '').toString();
  const algorithm: string = normalizeAlgorithm(rawAlgorithm);
  const hyper: Record<string, any> =
    sourceJob?.config?.hyperparameters || summary.hyperparameters || {};
  const featureColumns: string[] = summary.feature_columns || sourceJob?.config?.feature_columns || [];
  const targetColumn: string = summary.target_column || sourceJob?.config?.target_column || '—';

  const isDL = DL_ALGOS.has(algorithm);
  const isTree = TREE_ALGOS.has(algorithm);
  const isClassification = asset.problem_type === 'CLASSIFICATION';

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <InsightsIcon fontSize="small" color="secondary" />
          <Typography variant="subtitle2">
            v{version.version_number} · {humanize(algorithm || rawAlgorithm || 'model')}
          </Typography>
          <Chip size="small" label={version.stage} color={modelStageColor(version.stage)} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {summary.row_count ? `${summary.row_count.toLocaleString()} rows` : ''}
            {summary.training_time_sec ? ` · ${summary.training_time_sec}s` : ''}
          </Typography>
        </Stack>
      </Paper>

      {/* Detection diagnostic — tells user what the 3D view will render */}
      <Alert
        severity={isDL || isTree ? 'success' : 'info'}
        variant="outlined"
        sx={{ py: 0.5 }}
      >
        <Typography variant="caption">
          Algorithm detected: <b>{algorithm || '(unknown)'}</b>
          {rawAlgorithm && rawAlgorithm.toLowerCase() !== algorithm ? ` (from "${rawAlgorithm}")` : ''}
          {' · '}
          {isDL
            ? '3D neural-network view shown below'
            : isTree
              ? '3D tree-ensemble view shown below'
              : 'no known DL/tree structure — showing generic fallback'}
        </Typography>
      </Alert>

      {/* Key metrics */}
      <KeyMetricsRow metrics={metrics} isClassification={isClassification} />

      {/* Cross-validation */}
      {metrics.cross_validation && (
        <CrossValidationCard cv={metrics.cross_validation} />
      )}

      {/* Feature importance */}
      {metrics.feature_importance && (
        <FeatureImportanceChart
          importance={metrics.feature_importance}
          featureColumns={featureColumns}
        />
      )}

      {/* Confusion matrix */}
      {isClassification && metrics.confusion_matrix?.matrix && (
        <ConfusionMatrixHeatmap cm={metrics.confusion_matrix} />
      )}

      {/* Per-class precision / recall */}
      {isClassification && metrics.classification_report && (
        <ClassificationReportTable report={metrics.classification_report} />
      )}

      {/* DL layer stack */}
      {isDL && (
        <>
          <NeuralNetwork3D
            layers={deriveLayers(
              algorithm,
              hyper,
              summary.input_size || featureColumns.length,
              summary.output_size,
              summary.window_size,
            ).map((l): Layer3DSpec => ({
              label: l.label,
              units: l.units || 1,
              color: l.color,
              kind: l.kind,
              detail: l.detail,
            }))}
            height={360}
          />
          <NeuralNetworkDiagram
            algorithm={algorithm}
            hyper={hyper}
            inputSize={summary.input_size || featureColumns.length}
            outputSize={summary.output_size}
            windowSize={summary.window_size}
          />
        </>
      )}

      {/* Training curves */}
      {Array.isArray(metrics.training_history) && metrics.training_history.length > 0 && (
        <TrainingHistoryChart history={metrics.training_history} />
      )}

      {/* Tree ensemble diagram */}
      {isTree && (
        <>
          <NeuralNetwork3D
            layers={buildTreeEnsembleLayers(algorithm, hyper, featureColumns.length)}
            height={360}
          />
          <TreeEnsembleDiagram algorithm={algorithm} hyper={hyper} />
        </>
      )}

      {/* Generic fallback — always show a 3D view so the feature is visible */}
      {!isDL && !isTree && (
        <NeuralNetwork3D
          layers={[
            {
              label: 'Input features',
              units: Math.max(featureColumns.length, 4),
              color: '#1976d2',
            },
            { label: 'Model', units: 16, color: '#9c27b0', detail: rawAlgorithm || 'unknown' },
            {
              label: isClassification ? 'Class scores' : 'Prediction',
              units: isClassification ? 4 : 1,
              color: '#2e7d32',
            },
          ]}
          height={320}
        />
      )}

      {/* Raw hyperparameters fallback */}
      {Object.keys(hyper).length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="overline" color="text.secondary">
            Hyperparameters
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {Object.entries(hyper).map(([k, v]) => (
              <Chip
                key={k}
                size="small"
                variant="outlined"
                label={`${k}: ${String(v)}`}
                sx={{ height: 22, fontSize: 11 }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Dataset / target */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="overline" color="text.secondary">
          Training data
        </Typography>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          <Row k="Target" v={targetColumn} />
          <Row k="Features" v={String(featureColumns.length)} />
          <Row k="Dataset" v={version.dataset_version_name || `#${version.dataset_version}`} />
          {summary.device && <Row k="Device" v={summary.device} />}
        </Stack>
      </Paper>
    </Stack>
  );
};

/* ── Internals sub-components ── */

const KeyMetricsRow: React.FC<{ metrics: any; isClassification: boolean }> = ({
  metrics,
  isClassification,
}) => {
  const items = isClassification
    ? [
        ['Accuracy', metrics.accuracy],
        ['F1 (w)', metrics.f1_weighted ?? metrics.f1],
        ['Precision', metrics.precision_weighted ?? metrics.precision],
        ['Recall', metrics.recall_weighted ?? metrics.recall],
      ]
    : [
        ['R²', metrics.r2 ?? metrics.r2_score],
        ['MAE', metrics.mae],
        ['RMSE', metrics.rmse],
        ['MSE', metrics.mse],
      ];
  const filtered = items.filter(([, v]) => v !== undefined && v !== null);
  if (filtered.length === 0) return null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${filtered.length}, 1fr)`,
          gap: 1.5,
        }}
      >
        {filtered.map(([label, val]) => (
          <Box key={label as string} sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography variant="h6" sx={{ color: METRIC_COLOR, fontWeight: 700 }}>
              {fmtMetric(val)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const CrossValidationCard: React.FC<{ cv: any }> = ({ cv }) => {
  const scores: number[] = Array.isArray(cv.scores) ? cv.scores : [];
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Cross-validation
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="h6" sx={{ color: METRIC_COLOR, fontWeight: 700 }}>
          {fmtMetric(cv.mean)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ± {fmtMetric(cv.std)} · {scores.length || cv.folds || '—'} folds
        </Typography>
      </Stack>
      {scores.length > 0 && (
        <Box sx={{ mt: 1, height: 60 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={scores.map((s, i) => ({ fold: `F${i + 1}`, score: s }))}>
              <XAxis dataKey="fold" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 1]} />
              <RechartsTooltip
                formatter={(v: any) => fmtMetric(v)}
                contentStyle={{ fontSize: 11 }}
              />
              <Bar dataKey="score" fill={METRIC_COLOR} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

const FeatureImportanceChart: React.FC<{
  importance: any;
  featureColumns: string[];
}> = ({ importance, featureColumns }) => {
  // Accept various shapes: {feature: value}, {importances: [{name, value}]}, or array of numbers.
  let rows: Array<{ name: string; value: number }> = [];
  if (Array.isArray(importance)) {
    rows = importance.map((v: any, i: number) => ({
      name: featureColumns[i] || `f${i}`,
      value: Number(v) || 0,
    }));
  } else if (importance?.importances && Array.isArray(importance.importances)) {
    rows = importance.importances.map((entry: any) => ({
      name: entry.name || entry.feature || 'f',
      value: Number(entry.value ?? entry.importance ?? 0),
    }));
  } else if (typeof importance === 'object') {
    rows = Object.entries(importance)
      .filter(([, v]) => typeof v === 'number')
      .map(([k, v]) => ({ name: k, value: Number(v) }));
  }
  rows = rows
    .filter((r) => Number.isFinite(r.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, 15);

  if (rows.length === 0) return null;
  const maxVal = rows[0].value || 1;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Feature importance
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {rows.map((r) => (
          <Box key={r.name}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {r.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.value.toFixed(4)}
              </Typography>
            </Stack>
            <Box
              sx={{
                height: 6,
                bgcolor: 'action.hover',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${(r.value / maxVal) * 100}%`,
                  height: '100%',
                  bgcolor: IMPORTANCE_COLOR,
                  transition: 'width 0.3s',
                }}
              />
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const ConfusionMatrixHeatmap: React.FC<{ cm: any }> = ({ cm }) => {
  const matrix: number[][] = cm.matrix || [];
  const labels: string[] = cm.labels || matrix.map((_, i) => String(i));
  if (matrix.length === 0) return null;
  const flat = matrix.flat();
  const max = Math.max(...flat, 1);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Confusion matrix
      </Typography>
      <Box sx={{ mt: 1, overflowX: 'auto' }}>
        <Box
          sx={{
            display: 'inline-grid',
            gridTemplateColumns: `auto repeat(${labels.length}, 1fr)`,
            gap: 0.5,
          }}
        >
          {/* Header row */}
          <Box />
          {labels.map((l) => (
            <Typography
              key={`h-${l}`}
              variant="caption"
              align="center"
              sx={{ fontFamily: 'monospace', px: 0.5, fontWeight: 600 }}
            >
              {l}
            </Typography>
          ))}
          {/* Rows */}
          {matrix.map((row, i) => (
            <React.Fragment key={`r-${i}`}>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', px: 0.5, fontWeight: 600, alignSelf: 'center' }}
              >
                {labels[i]}
              </Typography>
              {row.map((cell, j) => {
                const intensity = cell / max;
                const isDiagonal = i === j;
                return (
                  <Box
                    key={`c-${i}-${j}`}
                    sx={{
                      minWidth: 38,
                      minHeight: 38,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 0.5,
                      bgcolor: isDiagonal
                        ? `rgba(76, 175, 80, ${0.15 + intensity * 0.7})`
                        : `rgba(244, 67, 54, ${0.08 + intensity * 0.5})`,
                      color: intensity > 0.5 ? '#fff' : 'text.primary',
                    }}
                  >
                    {cell}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 1 }}
      >
        Rows = true, columns = predicted · green = correct, red = errors
      </Typography>
    </Paper>
  );
};

const ClassificationReportTable: React.FC<{ report: any }> = ({ report }) => {
  const rows = Object.entries(report)
    .filter(
      ([k, v]: [string, any]) =>
        typeof v === 'object' && v !== null && 'precision' in v && k !== 'accuracy',
    )
    .map(([k, v]: [string, any]) => ({
      label: k,
      precision: v.precision,
      recall: v.recall,
      f1: v['f1-score'] ?? v.f1,
      support: v.support,
    }));
  if (rows.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Per-class performance
      </Typography>
      <Box sx={{ mt: 1, overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
        >
          <thead>
            <tr>
              {['Class', 'Precision', 'Recall', 'F1', 'Support'].map((h) => (
                <Box
                  key={h}
                  component="th"
                  sx={{
                    textAlign: h === 'Class' ? 'left' : 'right',
                    borderBottom: 1,
                    borderColor: 'divider',
                    pb: 0.5,
                    px: 1,
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  {h}
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <Box
                  component="td"
                  sx={{ py: 0.5, px: 1, fontFamily: 'monospace' }}
                >
                  {r.label}
                </Box>
                {[r.precision, r.recall, r.f1].map((v, i) => (
                  <Box
                    key={i}
                    component="td"
                    sx={{ py: 0.5, px: 1, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {fmtMetric(v)}
                  </Box>
                ))}
                <Box
                  component="td"
                  sx={{ py: 0.5, px: 1, textAlign: 'right', color: 'text.secondary' }}
                >
                  {r.support}
                </Box>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Paper>
  );
};

const TrainingHistoryChart: React.FC<{ history: any[] }> = ({ history }) => {
  // Expect [{epoch, loss, val_loss, accuracy?, val_accuracy?}, ...]
  const hasAcc = history.some((h) => h.accuracy !== undefined || h.val_accuracy !== undefined);
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Training history ({history.length} epochs)
      </Typography>
      <Box sx={{ mt: 1, height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={history}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="epoch" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <RechartsTooltip contentStyle={{ fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="loss" stroke="#f44336" dot={false} />
            <Line type="monotone" dataKey="val_loss" stroke="#ff9800" dot={false} />
            {hasAcc && (
              <>
                <Line type="monotone" dataKey="accuracy" stroke="#4caf50" dot={false} />
                <Line type="monotone" dataKey="val_accuracy" stroke="#2196f3" dot={false} />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

/* ── Neural network layer diagram ── */

type LayerSpec = {
  kind: string;
  label: string;
  units: number;
  color: string;
  detail?: string;
};

const deriveLayers = (
  algo: string,
  hp: Record<string, any>,
  inputSize: number,
  outputSize: number | undefined,
  windowSize: number | undefined,
): LayerSpec[] => {
  const inp = inputSize || Number(hp.input_size) || 0;
  const out = outputSize || Number(hp.output_size) || 1;
  const layers: LayerSpec[] = [];

  const push = (l: LayerSpec) => layers.push(l);

  push({
    kind: 'input',
    label: windowSize ? `Input (seq ${windowSize} × ${inp})` : `Input (${inp})`,
    units: inp || 8,
    color: '#1976d2',
  });

  const hs = Number(hp.hidden_size) || 64;
  const nl = Number(hp.num_layers) || 2;
  const d = Number(hp.d_model) || Number(hp.hidden_size) || 64;
  const nhead = Number(hp.nhead) || Number(hp.num_heads) || 4;
  const dff = Number(hp.dim_feedforward) || Number(hp.hidden_size) * 4 || 256;
  const dropout = Number(hp.dropout) || 0;
  const filters = Number(hp.filters) || Number(hp.num_channels) || 64;
  const kernel = Number(hp.kernel_size) || 3;

  const addDropout = () => {
    if (dropout > 0) {
      push({
        kind: 'dropout',
        label: `Dropout (${dropout})`,
        units: 0,
        color: '#9e9e9e',
      });
    }
  };

  if (algo === 'lstm' || algo === 'bilstm' || algo === 'gru') {
    const bi = algo === 'bilstm' || hp.bidirectional === 'true' || hp.bidirectional === true;
    for (let i = 0; i < nl; i++) {
      push({
        kind: 'recurrent',
        label: `${bi ? 'Bi-' : ''}${algo.toUpperCase()} layer ${i + 1}`,
        units: bi ? hs * 2 : hs,
        color: '#2e7d32',
        detail: `hidden ${hs}${bi ? ' ×2' : ''}`,
      });
      addDropout();
    }
  } else if (algo === 'transformer' || algo === 'attention_network') {
    for (let i = 0; i < nl; i++) {
      push({
        kind: 'attention',
        label: `Multi-Head Attention ${i + 1}`,
        units: d,
        color: '#7b1fa2',
        detail: `d_model ${d} · heads ${nhead}`,
      });
      push({
        kind: 'ff',
        label: `FeedForward ${i + 1}`,
        units: dff,
        color: '#ab47bc',
        detail: `dim ${dff}`,
      });
      addDropout();
    }
  } else if (algo === 'cnn_1d' || algo === 'tcn') {
    const ncl = Number(hp.num_conv_layers) || Number(hp.num_layers) || 3;
    for (let i = 0; i < ncl; i++) {
      push({
        kind: 'conv',
        label: `Conv1D ${i + 1}`,
        units: filters,
        color: '#ef6c00',
        detail: `filters ${filters} · k=${kernel}`,
      });
      addDropout();
    }
  } else if (algo === 'mlp') {
    const sizes = String(hp.hidden_layer_sizes || '100,50')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => n > 0);
    sizes.forEach((s, i) => {
      push({
        kind: 'dense',
        label: `Dense ${i + 1}`,
        units: s,
        color: '#2e7d32',
        detail: `${s} neurons · ReLU`,
      });
      addDropout();
    });
  } else {
    // Generic fallback: 2 hidden layers
    push({
      kind: 'hidden',
      label: 'Hidden layer 1',
      units: hs,
      color: '#2e7d32',
      detail: `${hs} neurons`,
    });
    addDropout();
    push({
      kind: 'hidden',
      label: 'Hidden layer 2',
      units: hs,
      color: '#2e7d32',
      detail: `${hs} neurons`,
    });
  }

  push({
    kind: 'output',
    label: `Output (${out})`,
    units: out,
    color: '#0288d1',
  });

  return layers;
};

/* ── Build 3D-friendly layer spec for tree ensembles ── */
const buildTreeEnsembleLayers = (
  algorithm: string,
  hyper: Record<string, any>,
  nFeatures: number,
): Layer3DSpec[] => {
  const nTrees = Number(hyper.n_estimators) || 100;
  const maxDepth = Math.max(2, Number(hyper.max_depth) || 6);
  const showTrees = Math.min(nTrees, 48);

  const layers: Layer3DSpec[] = [];
  layers.push({
    label: 'Input features',
    units: Math.max(nFeatures, 4),
    color: '#1976d2',
  });
  for (let d = 0; d < maxDepth; d++) {
    const nodesPerTree = Math.pow(2, d);
    const units = Math.min(showTrees * nodesPerTree, 64);
    layers.push({
      label: d === maxDepth - 1 ? 'Leaf nodes' : `Depth ${d + 1}`,
      units,
      color: d === maxDepth - 1 ? '#ff9800' : '#2e7d32',
      detail: `${showTrees} trees × ${nodesPerTree} node${nodesPerTree === 1 ? '' : 's'}`,
    });
  }
  layers.push({
    label: `${humanize(algorithm)} vote`,
    units: 1,
    color: '#0288d1',
    detail: 'aggregated prediction',
  });
  return layers;
};

const NeuralNetworkDiagram: React.FC<{
  algorithm: string;
  hyper: Record<string, any>;
  inputSize: number;
  outputSize?: number;
  windowSize?: number;
}> = ({ algorithm, hyper, inputSize, outputSize, windowSize }) => {
  const layers = useMemo(
    () => deriveLayers(algorithm, hyper, inputSize, outputSize, windowSize),
    [algorithm, hyper, inputSize, outputSize, windowSize],
  );
  const maxUnits = Math.max(...layers.map((l) => l.units || 1), 1);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Network layers
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Visualized from hyperparameters (illustrative; exact tensor shapes depend on the runtime)
      </Typography>
      <Stack spacing={0.5}>
        {layers.map((layer, idx) => {
          const displayUnits = Math.min(Math.max(layer.units, 1), 24);
          const neurons = Array.from({ length: displayUnits });
          const scale = Math.min(1, displayUnits / Math.min(maxUnits, 24));
          return (
            <React.Fragment key={idx}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.75,
                  borderLeft: `3px solid ${layer.color}`,
                  borderRadius: 0.5,
                  bgcolor: 'action.hover',
                }}
              >
                <Box sx={{ minWidth: 140 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: layer.color }}>
                    {layer.label}
                  </Typography>
                  {layer.detail && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>
                      {layer.detail}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    gap: 0.25,
                    flexWrap: 'wrap',
                    opacity: layer.kind === 'dropout' ? 0.4 : 1,
                  }}
                >
                  {neurons.map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 8 + scale * 4,
                        height: 8 + scale * 4,
                        borderRadius: '50%',
                        bgcolor: layer.color,
                        opacity: 0.5 + (i / Math.max(displayUnits - 1, 1)) * 0.5,
                      }}
                    />
                  ))}
                  {layer.units > 24 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                      ×{layer.units}
                    </Typography>
                  )}
                </Box>
              </Box>
              {idx < layers.length - 1 && (
                <Box
                  sx={{
                    alignSelf: 'center',
                    width: 2,
                    height: 10,
                    bgcolor: 'divider',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Paper>
  );
};

/* ── Tree ensemble diagram ── */

const TreeEnsembleDiagram: React.FC<{
  algorithm: string;
  hyper: Record<string, any>;
}> = ({ algorithm, hyper }) => {
  const nTrees = Number(hyper.n_estimators) || 100;
  const maxDepth = Number(hyper.max_depth) || 10;
  const minLeaf = Number(hyper.min_samples_leaf) || 1;
  const displayTrees = Math.min(nTrees, 24);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Ensemble structure
      </Typography>
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, mb: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Trees
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {nTrees}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Max depth
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {maxDepth}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Min leaf
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {minLeaf}
          </Typography>
        </Box>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {humanize(algorithm)} — showing {displayTrees} of {nTrees} estimators
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(displayTrees, 8)}, 1fr)`,
          gap: 1,
        }}
      >
        {Array.from({ length: displayTrees }).map((_, i) => (
          <MiniTree key={i} depth={Math.min(maxDepth, 4)} color="#2e7d32" />
        ))}
      </Box>
      {nTrees > displayTrees && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          + {nTrees - displayTrees} more
        </Typography>
      )}
    </Paper>
  );
};

const MiniTree: React.FC<{ depth: number; color: string }> = ({ depth, color }) => {
  // Render a simple binary tree as SVG.
  const levels = depth;
  const width = 60;
  const height = 14 * levels + 8;
  const nodes: Array<{ x: number; y: number }> = [];
  const edges: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];

  for (let lvl = 0; lvl < levels; lvl++) {
    const count = Math.pow(2, lvl);
    for (let i = 0; i < count; i++) {
      const x = ((i + 0.5) / count) * width;
      const y = 6 + lvl * 14;
      nodes.push({ x, y });
      if (lvl < levels - 1) {
        const leftX = ((2 * i + 0.5) / (count * 2)) * width;
        const rightX = ((2 * i + 1.5) / (count * 2)) * width;
        const childY = 6 + (lvl + 1) * 14;
        edges.push({ x1: x, y1: y, x2: leftX, y2: childY });
        edges.push({ x1: x, y1: y, x2: rightX, y2: childY });
      }
    }
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 0.5, p: 0.5 }}>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
        {edges.map((e, i) => (
          <line
            key={i}
            x1={e.x1}
            y1={e.y1}
            x2={e.x2}
            y2={e.y2}
            stroke={color}
            strokeWidth={0.5}
            opacity={0.5}
          />
        ))}
        {nodes.map((n, i) => (
          <circle key={i} cx={n.x} cy={n.y} r={1.5} fill={color} />
        ))}
      </svg>
    </Box>
  );
};

export default ModelLabAssetManagerPage;
